/*****************************************************************
 *  ZOHO BRIDGE PANEL — THE HEALTH STRIP AND THE FIELD PACK LINE   *
 *  File: apps/web/src/pages/bridge/BridgeHealth.js                *
 *  What it does: two small blocks under the page title:           *
 *    1. "Is the bridge working?" — when the bridge last checked   *
 *       in, when the newest record arrived, whether it was turned *
 *       away, and how the keys stand (defects 378, 392, 393);     *
 *    2. "Zoho fields in Customer Butler" — ready or not, with a   *
 *       button to set them up (phase 2b).                         *
 *  Reads: the summary the page already loaded.                    *
 *  Writes: POST /field-pack, only when a person confirms.         *
 *****************************************************************/

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 — THE HEALTH STRIP                                   *
 *  SECTION 2 — THE FIELD PACK LINE                                *
 *****************************************************************/

import React, { useState } from 'react';
import { BridgeIcon } from './bridgeIcons';
import { BRIDGE_WORDS, LOG_ACTION_NAMES } from './bridgeWords';
import { ToneChip, WhyBox, formatWhen } from './bridgeParts';
import { setUpFieldPack } from './bridgeApi';

/*****************************************************************
 *  SECTION 1 — THE HEALTH STRIP                                   *
 *****************************************************************/

// WHY (2026-10-13, help-run defect 378): the old strip only said when the
// newest NEW record arrived, so a dead bridge and a quiet Zoho looked the
// same. "Checked in" is the key's last good knock, which happens every run.

/* ---- HealthStrip({ summary }) ----------------------------------
   The bridge's health in words, each chip with a word and an
   icon (colour never alone).                                    */
export function HealthStrip({ summary }) {
  const health = summary.health || {};
  const keys = health.keys || {};
  const lastRefusal = health.lastRefusal;
  const showRefusal = Boolean(lastRefusal) && lastRefusal.isAfterLastKnock;
  const hasNoWorkingKey = Number(keys.working) === 0;

  return (
    <section className="zb-health" aria-labelledby="zb-health-title">
      <h2 id="zb-health-title" className="zb-subhead">
        <BridgeIcon name="pulse" />
        <span>{BRIDGE_WORDS.healthTitle}</span>
      </h2>

      <div className="zb-row zb-row-tight">
        {health.looksQuiet
          ? <ToneChip tone="warn" icon="alert" word={BRIDGE_WORDS.bridgeQuiet} />
          : <ToneChip tone="ok" icon="check" word={BRIDGE_WORDS.bridgeOk} />}
        {showRefusal ? (
          <ToneChip
            tone="bad"
            icon="cross"
            word={BRIDGE_WORDS.lastRefusalWord + ': ' + (LOG_ACTION_NAMES[lastRefusal.action] || lastRefusal.action)}
          />
        ) : null}
        <ToneChip tone="neutral" icon="key" word={keys.working + ' ' + BRIDGE_WORDS.keyWorkingCount} />
      </div>

      <dl className="zb-facts">
        <div className="zb-fact">
          <dt>{BRIDGE_WORDS.lastKnock}</dt>
          <dd>{health.lastKnockAt ? formatWhen(health.lastKnockAt) : BRIDGE_WORDS.neverKnocked}</dd>
        </div>
        <div className="zb-fact">
          <dt>{BRIDGE_WORDS.lastNewRecord}</dt>
          <dd>{summary.lastReceivedAt ? formatWhen(summary.lastReceivedAt) : BRIDGE_WORDS.neverHeard}</dd>
        </div>
        {showRefusal ? (
          <div className="zb-fact">
            <dt>{BRIDGE_WORDS.lastRefusalWord}</dt>
            <dd>{formatWhen(lastRefusal.at)}{lastRefusal.reason ? ' — ' + lastRefusal.reason : ''}</dd>
          </div>
        ) : null}
      </dl>

      {health.looksQuiet ? <p className="zb-quiet">{BRIDGE_WORDS.bridgeQuietWhy}</p> : null}

      {hasNoWorkingKey ? (
        <div className="zb-notice zb-notice-bad" role="alert">
          <BridgeIcon name="alert" />
          <span>{BRIDGE_WORDS.noWorkingKey}</span>
        </div>
      ) : null}

      {health.keyExpiresSoon ? (
        <div className="zb-notice zb-notice-warn" role="status">
          <BridgeIcon name="clock" />
          <span>{BRIDGE_WORDS.keyExpiresIn} {keys.daysToSoonestExpiry} {BRIDGE_WORDS.days}.</span>
        </div>
      ) : null}
    </section>
  );
}

/*****************************************************************
 *  SECTION 2 — THE FIELD PACK LINE                                *
 *****************************************************************/

/* ---- FieldPackLine({ fieldPack, onChanged }) -------------------
   Whether Customer Butler holds every Zoho field yet, with a
   "Set up the Zoho fields now" button (asks why first).         */
export function FieldPackLine({ fieldPack, onChanged }) {
  const [isWhyOpen, setIsWhyOpen] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [answerText, setAnswerText] = useState('');

  if (!fieldPack) {
    return null;
  }

  const expected = fieldPack.expected || {};
  const present = fieldPack.present || {};
  const countWords = 'Customers ' + (present.Customer || 0) + ' of ' + (expected.Customer || 0)
    + ', properties ' + (present.Property || 0) + ' of ' + (expected.Property || 0);

  /* ---- confirmSetUp(whyText) -- ask Customer Butler to hold them ---- */
  async function confirmSetUp(whyText) {
    setIsBusy(true);

    const answer = await setUpFieldPack(whyText);

    setIsBusy(false);
    setIsWhyOpen(false);
    setAnswerText(answer.ok ? BRIDGE_WORDS.fieldPackDone : answer.message || BRIDGE_WORDS.loadFailed);
    onChanged();
  }

  return (
    <section className="zb-block" aria-labelledby="zb-fieldpack-title">
      <h2 id="zb-fieldpack-title" className="zb-subhead">
        <BridgeIcon name="stack" />
        <span>{BRIDGE_WORDS.fieldPackTitle}</span>
      </h2>
      <div className="zb-row zb-row-tight">
        {fieldPack.isComplete
          ? <ToneChip tone="ok" icon="check" word={BRIDGE_WORDS.fieldPackReady} />
          : <ToneChip tone="warn" icon="clock" word={BRIDGE_WORDS.fieldPackMissing} />}
        <span className="zb-small">{countWords}</span>
        {!fieldPack.isComplete && !isWhyOpen ? (
          <button type="button" className="zb-button zb-button-plain" onClick={() => setIsWhyOpen(true)}>
            {BRIDGE_WORDS.fieldPackButton}
          </button>
        ) : null}
      </div>
      {isWhyOpen ? (
        <WhyBox
          confirmWord={BRIDGE_WORDS.fieldPackButton}
          tone="primary"
          busy={isBusy}
          onConfirm={confirmSetUp}
          onCancel={() => setIsWhyOpen(false)}
        />
      ) : null}
      {answerText ? <p className="zb-quiet" role="status">{answerText}</p> : null}
    </section>
  );
}
